import React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Reusable round info "i" button that opens a small popover with a
 * contextual tip. Used across the app to teach first-time users what
 * each section does without cluttering the screen with help copy.
 *
 * Props:
 *  - title?: string           Optional bold title at the top of the tip
 *  - tip: string | JSX        The body text (required)
 *  - testId?: string          data-testid for the trigger button
 *  - size?: 'sm' | 'md'       sm = 22px, md = 30px (default sm)
 *  - align?: 'start'|'center'|'end'   popover alignment (default 'start')
 *  - color?: string           Accent color of the icon ring (default purple)
 */
export default function InfoPopoverButton({
  title,
  tip,
  testId,
  size = 'sm',
  align = 'start',
  color = 'var(--accent-purple)',
  ariaLabel,
}) {
  const px = size === 'md' ? 30 : 22;
  const iconPx = size === 'md' ? 18 : 14;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel || title || 'More info'}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: px,
            height: px,
            borderRadius: '50%',
            border: `1.5px solid ${color}`,
            background: 'transparent',
            color: color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            padding: 0,
            transition: 'transform 150ms ease, background 150ms ease',
          }}
          onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; }}
          onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <Info size={iconPx} strokeWidth={1.8} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={10}
        data-testid={testId ? `${testId}-popover` : undefined}
        className="border-0 bg-transparent p-0 shadow-none"
        style={{ maxWidth: 'min(86vw, 320px)' }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 18,
            boxShadow: 'var(--premium-shadow-main)',
            padding: '14px 16px',
          }}
        >
          {title ? (
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 6px' }}>
              {title}
            </p>
          ) : null}
          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, margin: 0 }}>
            {tip}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
